import React, { useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  Tooltip,
  Link,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CancelIcon from '@mui/icons-material/Cancel';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { UserContext } from '../../components/shared/useUser';
import {
  TasksClient,
  TaskSummaryDto,
  TaskStatusesClient,
  TaskPrioritiesClient,
  TaskStatusDto,
  TaskPriorityDto,
  TaskSourceType,
} from '../../app/services/api/tasksClient';

const tasksClient = TasksClient;
const statusesClient = TaskStatusesClient;
const prioritiesClient = TaskPrioritiesClient;

// Timeline lane representing a category row within a source type
interface TimelineLane {
  categoryName: string;
  tasks: TaskSummaryDto[];
}

// Task source type groups
interface TaskSourceGroup {
  sourceType: TaskSourceType;
  displayName: string;
  lanes: TimelineLane[];
}

// Define the order and display names for task source types
const TASK_SOURCE_TYPE_CONFIG: { type: TaskSourceType; displayName: string }[] = [
  { type: 'OperationalDay', displayName: 'Operational Day' },
  { type: 'Scheduled', displayName: 'Scheduled' },
  { type: 'System', displayName: 'System' },
  { type: 'Workflow', displayName: 'Workflow' },
  { type: 'AdHoc', displayName: 'Ad-Hoc' },
];

const OperationsClockDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasPermissionTo, stores, selectedStore, setSelectedStore } = useContext(UserContext);
  const canView = hasPermissionTo ? hasPermissionTo(['OperationalDaysView']) : true;

  const [zoomLevel, setZoomLevel] = useState(1);
  const [currentTime, setCurrentTime] = useState(new Date());
  const timelineRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [tasks, setTasks] = useState<TaskSummaryDto[]>([]);
  const [statuses, setStatuses] = useState<TaskStatusDto[]>([]);
  const [priorities, setPriorities] = useState<TaskPriorityDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Calculate container width for auto-fit (default to 1200px before measurement)
  const [containerWidth, setContainerWidth] = useState(1200);

  // Store opening and closing times (could come from API in future)
  const openingHour = 6; // 6 AM
  const closingHour = 22; // 10 PM

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        // Subtract category label width (140px) and some padding
        const newWidth = containerRef.current.offsetWidth - 180;
        if (newWidth > 0) {
          setContainerWidth(newWidth);
          console.log('[Operations Clock] Container width updated:', newWidth);
        }
      }
    };
    // Use setTimeout to ensure DOM is rendered
    const timer = setTimeout(updateWidth, 0);
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Fetch statuses and priorities on mount
  useEffect(() => {
    statusesClient.getStatuses().then(setStatuses).catch(() => setStatuses([]));
    prioritiesClient.getPriorities().then(setPriorities).catch(() => setPriorities([]));
  }, []);

  // Fetch tasks when store changes (or on mount with default store for demo)
  useEffect(() => {
    // Use selected store or default to mock store number for demo purposes
    const storeNumber = selectedStore?.storeNumber ?? '1002804';
    const franchiseName = selectedStore?.franchiseName ?? '';

    setIsLoading(true);

    // Get tasks for today
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    tasksClient
      .getStoreTasks(storeNumber, franchiseName, startOfDay.toISOString(), endOfDay.toISOString())
      .then((loadedTasks) => {
        console.log('Operations Clock - Loaded tasks:', loadedTasks.length, loadedTasks);
        setTasks(loadedTasks);
      })
      .catch((err) => {
        console.error('Operations Clock - Error loading tasks:', err);
        setTasks([]);
      })
      .finally(() => setIsLoading(false));
  }, [selectedStore?.storeNumber, selectedStore?.franchiseName]);

  // Group tasks by task source type and category
  const sourceGroups: TaskSourceGroup[] = useMemo(() => {
    console.log('Operations Clock - Grouping tasks:', tasks.length);

    const groups: TaskSourceGroup[] = [];

    TASK_SOURCE_TYPE_CONFIG.forEach(config => {
      const tasksForType = tasks.filter(t => (t.taskSourceType ?? 'Scheduled') === config.type);

      if (tasksForType.length === 0) return;

      // Group by category within this source type
      const categoryMap = new Map<string, TaskSummaryDto[]>();
      tasksForType.forEach(task => {
        const category = task.categoryName ?? 'Other';
        if (!categoryMap.has(category)) {
          categoryMap.set(category, []);
        }
        categoryMap.get(category)!.push(task);
      });

      // Convert to lanes array
      const lanes: TimelineLane[] = Array.from(categoryMap.entries())
        .map(([categoryName, categoryTasks]) => ({
          categoryName,
          tasks: categoryTasks,
        }))
        .sort((a, b) => a.categoryName.localeCompare(b.categoryName));

      groups.push({
        sourceType: config.type,
        displayName: config.displayName,
        lanes,
      });
    });

    console.log('Operations Clock - Source groups:', groups.length, groups);
    return groups;
  }, [tasks]);

  // Calculate timeline dimensions - auto-fit to container width
  // Day runs from midnight to midnight to permit tasks before opening and after closing
  const startHour = 0; // Midnight
  const endHour = 24; // Midnight (end of day)
  const totalMinutes = (endHour - startHour) * 60;

  // Calculate pixels per minute to fit in container width
  const pixelsPerMinute = useMemo(() => {
    if (containerWidth <= 0) return 1;
    const basePixelsPerMinute = containerWidth / totalMinutes;
    const result = basePixelsPerMinute * zoomLevel;
    console.log('[Operations Clock] Calculated pixelsPerMinute:', result, '(containerWidth:', containerWidth, 'zoom:', zoomLevel, ')');
    return result;
  }, [containerWidth, totalMinutes, zoomLevel]);

  const timelineWidth = totalMinutes * pixelsPerMinute;

  // Parse time from ISO date string
  const parseTime = (isoString: string | undefined | null): { hours: number; minutes: number } | null => {
    if (!isoString) return null;
    const date = new Date(isoString);
    return { hours: date.getHours(), minutes: date.getMinutes() };
  };

  // Convert time to position
  const timeToPosition = useCallback((hours: number, minutes: number): number => {
    const totalMins = Math.max(0, (hours - startHour) * 60 + minutes);
    return Math.min(totalMins * pixelsPerMinute, timelineWidth);
  }, [pixelsPerMinute, timelineWidth, startHour]);

  // Get current time position
  const getCurrentTimePosition = (): number => {
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    if (hours < startHour || hours >= endHour) return -1;
    return timeToPosition(hours, minutes);
  };

  // Generate time markers
  const timeMarkers = useMemo(() => {
    const markers = [];
    for (let hour = startHour; hour <= endHour; hour += 2) {
      const position = (hour - startHour) * 60 * pixelsPerMinute;
      let label: string;
      if (hour === 0 || hour === 24) {
        label = '12am';
      } else if (hour < 12) {
        label = `${hour}am`;
      } else if (hour === 12) {
        label = '12pm';
      } else {
        label = `${hour - 12}pm`;
      }
      markers.push({ position, label, hour });
    }
    return markers;
  }, [pixelsPerMinute, startHour, endHour]);

  // Calculate opening and closing line positions
  const openingPosition = useMemo(() => {
    return (openingHour - startHour) * 60 * pixelsPerMinute;
  }, [openingHour, startHour, pixelsPerMinute]);

  const closingPosition = useMemo(() => {
    return (closingHour - startHour) * 60 * pixelsPerMinute;
  }, [closingHour, startHour, pixelsPerMinute]);

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.25, 0.5));

  const handleStoreChange = (event: SelectChangeEvent<string>) => {
    const store = stores.find(s => s.storeNumber === event.target.value);
    if (store && setSelectedStore) {
      setSelectedStore(store);
    }
  };

  const handleTaskClick = (taskId: number) => {
    navigate(`/tasks/${taskId}`);
  };

  const getStatusColor = (statusId: number): string => {
    const status = statuses.find(s => s.id === statusId);
    switch (status?.name?.toLowerCase()) {
      case 'completed':
      case 'approved':
        return theme.palette.success.main;
      case 'in progress':
        return theme.palette.info.main;
      case 'pending approval':
        return theme.palette.warning.main;
      case 'rejected':
        return theme.palette.error.main;
      case 'open':
      default:
        return theme.palette.custom.gray[500];
    }
  };

  const getStatusIcon = (statusId: number) => {
    const status = statuses.find(s => s.id === statusId);
    switch (status?.name?.toLowerCase()) {
      case 'completed':
      case 'approved':
        return <CheckCircleIcon fontSize="small" />;
      case 'in progress':
        return <PlayCircleFilledIcon fontSize="small" />;
      case 'pending approval':
        return <PendingActionsIcon fontSize="small" />;
      case 'rejected':
        return <CancelIcon fontSize="small" />;
      case 'open':
      default:
        return <HourglassBottomIcon fontSize="small" />;
    }
  };

  const getPriorityBorderColor = (priorityId: number): string => {
    const priority = priorities.find(p => p.id === priorityId);
    switch (priority?.name?.toLowerCase()) {
      case 'emergency':
        return theme.palette.error.dark;
      case 'high':
        return theme.palette.error.main;
      case 'medium':
        return theme.palette.warning.main;
      case 'low':
      default:
        return theme.palette.custom.gray[400];
    }
  };

  const getPriorityIcon = (priorityId: number) => {
    const priority = priorities.find(p => p.id === priorityId);
    switch (priority?.name?.toLowerCase()) {
      case 'emergency':
        return <ErrorIcon fontSize="small"
          sx={{ color: theme.palette.error.dark }} />;
      case 'high':
        return <PriorityHighIcon fontSize="small"
          sx={{ color: theme.palette.error.main }} />;
      case 'medium':
        return <ScheduleIcon fontSize="small"
          sx={{ color: theme.palette.warning.main }} />;
      case 'low':
      default:
        return <AccessTimeIcon fontSize="small"
          sx={{ color: theme.palette.custom.gray[600] }} />;
    }
  };

  const getStatusName = (statusId: number): string => {
    return statuses.find(s => s.id === statusId)?.name ?? 'Unknown';
  };

  const getPriorityName = (priorityId: number): string => {
    return priorities.find(p => p.id === priorityId)?.name ?? 'Unknown';
  };

  const isTaskOverdue = (task: TaskSummaryDto): boolean => {
    if (!task.dueDateUtc) return false;
    if (task.statusId === 6 || task.statusId === 4) return false; // Completed or Approved
    return new Date(task.dueDateUtc) < currentTime;
  };

  const isHighOrEmergencyPriority = (priorityId: number): boolean => {
    const priority = priorities.find(p => p.id === priorityId);
    return priority?.name?.toLowerCase() === 'emergency' || priority?.name?.toLowerCase() === 'high';
  };

  // Calculate completion stats - statusId 6 is Completed, 4 is Approved
  const completedTasks = tasks.filter(t => t.statusId === 6 || t.statusId === 4).length;
  const totalTasks = tasks.length;
  const completionPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 100;

  if (!canView) {
    return (
      <PageArea>
        <PageTitle>Operations Clock</PageTitle>
        <Typography>You do not have permission to view the operations clock.</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Box
        ref={containerRef}
        sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <PageTitle subTitle="Daily operations overview">Operations Clock</PageTitle>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Tooltip title="Zoom in">
              <IconButton
                onClick={handleZoomIn}
                size="small"
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom out">
              <IconButton
                onClick={handleZoomOut}
                size="small"
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            </Tooltip>

            {/* Store Selector */}
            <FormControl
              size="small"
              sx={{ minWidth: 200 }}
            >
              <InputLabel id="store-select-label">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <StorefrontIcon fontSize="small" />
                  Store
                </Box>
              </InputLabel>
              <Select
                labelId="store-select-label"
                id="store-select"
                value={selectedStore?.storeNumber ?? ''}
                label="Store"
                onChange={handleStoreChange}
              >
                {stores.map(store => (
                  <MenuItem
                    key={store.storeNumber}
                    value={store.storeNumber}
                  >
                    {store.storeName ?? store.storeNumber}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Main Timeline Section */}
        <Section style={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', minHeight: Math.max(300, sourceGroups.reduce((sum, g) => sum + g.lanes.length, 0) * 56 + 80) }}>
            {/* Timeline Content */}
            <Box
              ref={timelineRef}
              sx={{
                flex: 1,
                overflowX: zoomLevel > 1 ? 'auto' : 'hidden',
                overflowY: 'auto',
                position: 'relative',
              }}
            >
              {/* Time Markers Header */}
              <Box
                sx={{
                  display: 'flex',
                  position: 'sticky',
                  top: 0,
                  backgroundColor: theme.palette.background.paper,
                  zIndex: 10,
                  borderBottom: `1px solid ${theme.palette.custom.gray[200]}`,
                  height: 32,
                  width: timelineWidth + 180,
                  minWidth: '100%',
                }}
              >
                {/* Task Type + Category label header spacer */}
                <Box
                  sx={{
                    width: 180,
                    minWidth: 180,
                    borderRight: `1px solid ${theme.palette.custom.gray[200]}`,
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="textSM"
                    fontWeight={600}
                    sx={{ color: theme.palette.custom.gray[600], minWidth: 60 }}
                  >
                    Type
                  </Typography>
                  <Typography
                    variant="textSM"
                    fontWeight={600}
                    sx={{ color: theme.palette.custom.gray[600] }}
                  >
                    Category
                  </Typography>
                </Box>
                <Box sx={{ position: 'relative', flex: 1 }}>
                  {timeMarkers.map(marker => (
                    <Typography
                      key={marker.hour}
                      variant="textSM"
                      sx={{
                        position: 'absolute',
                        left: marker.position,
                        transform: 'translateX(-50%)',
                        color: theme.palette.custom.gray[600],
                        fontWeight: 500,
                        top: 8,
                      }}
                    >
                      {marker.label}
                    </Typography>
                  ))}
                </Box>
              </Box>

              {/* Source Type Groups + Category Lanes */}
              <Box sx={{ position: 'relative', width: timelineWidth + 180, minWidth: '100%' }}>
                {isLoading ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">Loading tasks...</Typography>
                  </Box>
                ) : sourceGroups.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      No tasks found for today (Tasks loaded: {tasks.length})
                    </Typography>
                  </Box>
                ) : (
                  sourceGroups.map((group) => {
                    let globalLaneIndex = 0;
                    return group.lanes.map((lane, laneIndexInGroup) => {
                      const isFirstLaneInGroup = laneIndexInGroup === 0;
                      const laneCount = group.lanes.length;
                      const laneIndex = globalLaneIndex++;

                      return (
                        <Box
                          key={`${group.sourceType}-${lane.categoryName}`}
                          sx={{
                            height: 56,
                            position: 'relative',
                            borderBottom: `1px solid ${theme.palette.custom.gray[100]}`,
                            backgroundColor: laneIndex % 2 === 0 ? 'transparent' : theme.palette.custom.gray[50],
                            display: 'flex',
                          }}
                        >
                          {/* Task Source Type Label (vertical, spans multiple rows, sticky) */}
                          {isFirstLaneInGroup && (
                            <Box
                              sx={{
                                position: 'sticky',
                                left: 0,
                                top: 0,
                                width: 60,
                                height: laneCount * 56,
                                borderRight: `2px solid ${theme.palette.custom.gray[300]}`,
                                backgroundColor: theme.palette.custom.gray[100],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 30,
                              }}
                            >
                              <Typography
                                variant="textSM"
                                fontWeight={600}
                                sx={{
                                  color: theme.palette.custom.gray[700],
                                  writingMode: 'vertical-rl',
                                  textOrientation: 'mixed',
                                  transform: 'rotate(180deg)',
                                  letterSpacing: '0.05em',
                                }}
                              >
                                {group.displayName}
                              </Typography>
                            </Box>
                          )}

                          {/* Category Label */}
                          <Box
                            sx={{
                              position: 'sticky',
                              left: 60,
                              width: 120,
                              minWidth: 120,
                              backgroundColor: laneIndex % 2 === 0 ? theme.palette.background.paper : theme.palette.custom.gray[50],
                              borderRight: `1px solid ${theme.palette.custom.gray[200]}`,
                              display: 'flex',
                              alignItems: 'center',
                              px: 1,
                              zIndex: 30,
                            }}
                          >
                            <Typography
                              variant="textSM"
                              fontWeight={500}
                              noWrap
                              sx={{ color: theme.palette.custom.gray[700] }}
                            >
                              {lane.categoryName}
                            </Typography>
                          </Box>

                          {/* Tasks Timeline */}
                          <Box sx={{ flex: 1, position: 'relative' }}>
                            {(() => {
                              console.log('[Operations Clock] Rendering lane:', group.displayName, '-', lane.categoryName, 'with', lane.tasks.length, 'tasks');
                              // Sort tasks by start time for stacking calculation
                              const sortedTasks = [...lane.tasks].sort((a, b) => {
                                const aStart = parseTime(a.startDateUtc) ?? parseTime(a.dueDateUtc) ?? { hours: 9, minutes: 0 };
                                const bStart = parseTime(b.startDateUtc) ?? parseTime(b.dueDateUtc) ?? { hours: 9, minutes: 0 };
                                return (aStart.hours * 60 + aStart.minutes) - (bStart.hours * 60 + bStart.minutes);
                              });

                              // Track placed pills for overlap detection
                              const placedPills: Array<{ left: number; right: number; top: number }> = [];
                              const pillHeight = 40;
                              const pillGap = 4;

                              return sortedTasks.map(task => {
                                // Start time: use startDateUtc, fallback to dueDateUtc minus 1 hour, then default
                                const dueTime = parseTime(task.dueDateUtc);
                                const startTime = parseTime(task.startDateUtc)
                                  ?? (dueTime ? { hours: Math.max(0, dueTime.hours - 1), minutes: dueTime.minutes } : { hours: 9, minutes: 0 });
                                const endTime = dueTime ?? { hours: Math.min(startTime.hours + 1, endHour), minutes: startTime.minutes };

                                const left = timeToPosition(startTime.hours, startTime.minutes);
                                const right = timeToPosition(endTime.hours, endTime.minutes);
                                const width = Math.max(right - left, 80);

                                console.log('[Operations Clock] Rendering task:', {
                                  id: task.id,
                                  title: task.title,
                                  dueTime,
                                  startTime,
                                  endTime,
                                  left,
                                  right,
                                  width,
                                  pixelsPerMinute,
                                  timelineWidth,
                                });

                                // Find stack level by checking overlaps with previously placed pills
                                let stackLevel = 0;
                                const actualRight = left + width;
                                for (let level = 0; level < 10; level++) {
                                  const hasOverlap = placedPills.some(
                                    (pill) =>
                                      pill.top === level * (pillHeight + pillGap) + 8 &&
                                      !(actualRight <= pill.left || left >= pill.right),
                                  );
                                  if (!hasOverlap) {
                                    stackLevel = level;
                                    break;
                                  }
                                }
                                const top = stackLevel * (pillHeight + pillGap) + 8;
                                placedPills.push({ left, right: actualRight, top });

                                return (
                                  <Tooltip
                                    key={task.id}
                                    title={
                                      <Box sx={{ py: 0.5 }}>
                                        <Typography
                                          variant="textMD"
                                          fontWeight={700}
                                          sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid rgba(255,255,255,0.2)' }}
                                        >
                                          {task.title}
                                        </Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                          {getStatusIcon(task.statusId)}
                                          <Typography variant="textSM"
                                            fontWeight={600}>
                                            Status:
                                          </Typography>
                                          <Typography variant="textSM">
                                            {getStatusName(task.statusId)}
                                          </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                          {getPriorityIcon(task.priorityId)}
                                          <Typography variant="textSM"
                                            fontWeight={600}>
                                            Priority:
                                          </Typography>
                                          <Typography variant="textSM">
                                            {getPriorityName(task.priorityId)}
                                          </Typography>
                                        </Box>

                                        {(task.startDateUtc || task.dueDateUtc) && (
                                          <Box sx={{ pt: 1, mt: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                                            {task.startDateUtc && (
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography variant="textSM"
                                                  fontWeight={600}
                                                  sx={{ minWidth: 50 }}>
                                                  Start:
                                                </Typography>
                                                <Typography variant="textSM">
                                                  {new Date(task.startDateUtc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </Typography>
                                              </Box>
                                            )}
                                            {task.dueDateUtc && (
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="textSM"
                                                  fontWeight={600}
                                                  sx={{ minWidth: 50 }}>
                                                  Due:
                                                </Typography>
                                                <Typography variant="textSM">
                                                  {new Date(task.dueDateUtc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </Typography>
                                              </Box>
                                            )}
                                          </Box>
                                        )}

                                        <Typography
                                          variant="textSM"
                                          sx={{ mt: 1.5, pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)', fontStyle: 'italic', opacity: 0.9 }}
                                        >
                                          Click to view full details
                                        </Typography>
                                      </Box>
                                    }
                                    arrow
                                    enterDelay={200}
                                  >
                                    <Box
                                      onClick={() => handleTaskClick(task.id)}
                                      sx={{
                                        position: 'absolute',
                                        left,
                                        width,
                                        height: pillHeight,
                                        top,
                                        backgroundColor: (() => {
                                          const statusColor = getStatusColor(task.statusId);
                                          // Use lighter, more transparent backgrounds for better readability
                                          const status = statuses.find(s => s.id === task.statusId);
                                          switch (status?.name?.toLowerCase()) {
                                            case 'completed':
                                            case 'approved':
                                              return theme.palette.success.light + '50';
                                            case 'in progress':
                                              return theme.palette.info.light + '50';
                                            case 'pending approval':
                                              return theme.palette.warning.light + '50';
                                            case 'rejected':
                                              return theme.palette.error.light + '50';
                                            case 'open':
                                            default:
                                              return theme.palette.custom.gray[300] + '80';
                                          }
                                        })(),
                                        backgroundImage: isTaskOverdue(task)
                                          ? 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(211, 47, 47, 0.1) 10px, rgba(211, 47, 47, 0.1) 20px)'
                                          : 'none',
                                        borderRadius: '8px',
                                        borderLeft: `6px solid ${getPriorityBorderColor(task.priorityId)}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        pl: 2,
                                        pr: 1.5,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
                                        overflow: 'visible',
                                        zIndex: 5,
                                        border: (() => {
                                          const priority = priorities.find(p => p.id === task.priorityId);
                                          const isEmergency = priority?.name?.toLowerCase() === 'emergency';
                                          const isHigh = priority?.name?.toLowerCase() === 'high';

                                          if (isEmergency) return `2px solid ${theme.palette.error.dark}`;
                                          if (isHigh) return `2px solid ${theme.palette.error.main}`;
                                          return `1px solid ${getStatusColor(task.statusId)}40`;
                                        })(),
                                        animation: (() => {
                                          const isOverdue = isTaskOverdue(task);
                                          const isEmergency = priorities.find(p => p.id === task.priorityId)?.name?.toLowerCase() === 'emergency';
                                          if (isOverdue) return 'pulse-overdue 2s ease-in-out infinite';
                                          if (isEmergency && !isOverdue) return 'pulse-emergency 1.5s ease-in-out infinite';
                                          return 'none';
                                        })(),
                                        '@keyframes pulse-overdue': {
                                          '0%, 100%': {
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.12), 0 0 0 0 rgba(211, 47, 47, 0.7)',
                                          },
                                          '50%': {
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.12), 0 0 0 4px rgba(211, 47, 47, 0)',
                                          },
                                        },
                                        '@keyframes pulse-emergency': {
                                          '0%, 100%': {
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.12), 0 0 0 0 rgba(183, 28, 28, 0.6)',
                                          },
                                          '50%': {
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.12), 0 0 0 3px rgba(183, 28, 28, 0)',
                                          },
                                        },
                                        '&:hover': {
                                          transform: 'translateY(-2px)',
                                          boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                          zIndex: 25,
                                          backgroundColor: (() => {
                                            const status = statuses.find(s => s.id === task.statusId);
                                            switch (status?.name?.toLowerCase()) {
                                              case 'completed':
                                              case 'approved':
                                                return theme.palette.success.light + '80';
                                              case 'in progress':
                                                return theme.palette.info.light + '80';
                                              case 'pending approval':
                                                return theme.palette.warning.light + '80';
                                              case 'rejected':
                                                return theme.palette.error.light + '80';
                                              case 'open':
                                              default:
                                                return theme.palette.custom.gray[300];
                                            }
                                          })(),
                                        },
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                        <Typography
                                          variant="textSM"
                                          noWrap
                                          sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 600,
                                            fontSize: '11px',
                                            zIndex: 1,
                                            flex: 1,
                                          }}
                                        >
                                          {task.title}
                                        </Typography>
                                      </Box>

                                      {/* Status Badge - Floating Bottom Right */}
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          bottom: -10,
                                          right: -10,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: getStatusColor(task.statusId),
                                          color: theme.palette.common.white,
                                          borderRadius: '50%',
                                          width: 24,
                                          height: 24,
                                          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                          zIndex: 10,
                                        }}
                                      >
                                        {getStatusIcon(task.statusId)}
                                      </Box>

                                      {/* Overdue Badge - Floating Bottom Left */}
                                      {isTaskOverdue(task) && (
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            bottom: -8,
                                            left: -8,
                                            backgroundColor: theme.palette.error.main,
                                            color: theme.palette.common.white,
                                            borderRadius: '10px',
                                            padding: '1px 5px',
                                            fontSize: '9px',
                                            fontWeight: 700,
                                            letterSpacing: '0.5px',
                                            textTransform: 'uppercase',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                                            zIndex: 11,
                                          }}
                                        >
                                          Overdue
                                        </Box>
                                      )}
                                    </Box>
                                  </Tooltip>
                                );
                              });
                            })()}
                          </Box>
                        </Box>
                      );
                    });
                  })
                )}

                {/* Current Time Indicator */}
                {getCurrentTimePosition() >= 0 && sourceGroups.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: getCurrentTimePosition() + 180,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      backgroundColor: theme.palette.error.main,
                      zIndex: 20,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -6,
                        left: -5,
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.error.main,
                      },
                    }}
                  />
                )}

                {/* Hour Grid Lines - enhanced for Gantt chart look */}
                {sourceGroups.length > 0 && timeMarkers.map((marker, idx) => (
                  <Box
                    key={`grid-${marker.hour}`}
                    sx={{
                      position: 'absolute',
                      left: marker.position + 180,
                      top: 0,
                      bottom: 0,
                      width: idx % 2 === 0 ? 2 : 1,
                      backgroundColor: idx % 2 === 0 ? theme.palette.custom.gray[300] : theme.palette.custom.gray[200],
                      zIndex: 1,
                    }}
                  />
                ))}
                {/* Background shading for working hours (Gantt-style) */}
                {sourceGroups.length > 0 && (
                  <>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: openingPosition + 180,
                        top: 0,
                        bottom: 0,
                        width: closingPosition - openingPosition,
                        backgroundColor: theme.palette.success.light + '08',
                        zIndex: 0,
                      }}
                    />
                  </>
                )}

                {/* Opening Time Vertical Bar */}
                {sourceGroups.length > 0 && (
                  <Tooltip
                    title={`Opening: ${openingHour}:00`}
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: openingPosition + 180,
                        top: 0,
                        bottom: 0,
                        width: 3,
                        backgroundColor: theme.palette.success.main,
                        zIndex: 15,
                        cursor: 'pointer',
                        '&::before': {
                          content: '"Opening"',
                          position: 'absolute',
                          bottom: -24,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '10px',
                          fontWeight: 600,
                          color: theme.palette.success.main,
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </Tooltip>
                )}

                {/* Closing Time Vertical Bar */}
                {sourceGroups.length > 0 && (
                  <Tooltip
                    title={`Closing: ${closingHour}:00`}
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: closingPosition + 180,
                        top: 0,
                        bottom: 0,
                        width: 3,
                        backgroundColor: theme.palette.custom.gray[600],
                        zIndex: 15,
                        cursor: 'pointer',
                        '&::before': {
                          content: '"Closing"',
                          position: 'absolute',
                          bottom: -24,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '10px',
                          fontWeight: 600,
                          color: theme.palette.custom.gray[600],
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        </Section>

        {/* Compliance Footer */}
        <Section style={{ py: 2, px: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              variant="textMD"
              sx={{ color: theme.palette.custom.gray[700] }}
            >
              Task completion status
            </Typography>
            <CheckCircleOutlineIcon
              sx={{
                fontSize: 28,
                color: completionPercentage >= 80 ? theme.palette.success.main : theme.palette.warning.main,
              }}
            />
            <Typography variant="textMD">
              {completedTasks} of {totalTasks} tasks completed ({' '}
              <Box
                component="span"
                sx={{
                  color: completionPercentage >= 80 ? theme.palette.success.main : theme.palette.warning.main,
                  fontWeight: 600,
                }}
              >
                {completionPercentage}%
              </Box>
              {' '}).{' '}
              <Link
                href="/tasks"
                underline="always"
                sx={{ cursor: 'pointer' }}
              >
                View all tasks
              </Link>
            </Typography>
          </Box>
        </Section>
      </Box>
    </PageArea>
  );
};

export default OperationsClockDashboard;
